/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import RenewalModal from '../../components/shared/RenewalModal';
import PlatformPerformanceCard from '../../components/vendor/PlatformPerformanceCard';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import VendorScheduleAlerts from '../../components/vendor/VendorScheduleAlerts';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import useVendorGettingStarted from '../../hooks/useVendorGettingStarted';
import { VendorGettingStarted, VendorPageHeader } from '../../components/vendor/guidance';
import {
 Calendar,
 DollarSign,
 Star,
 Crown,
 ArrowRight,
 Clock,
 Eye,
 Loader2,
 RefreshCw,
 Video,
 BarChart3,
 Banknote,
 FileText,
 Globe,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ChannelBadge from '../../components/shared/ChannelBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import useDashboardAmountVisibility from '../../hooks/useDashboardAmountVisibility';
import DashboardAmountVisibilityToggle from '../../components/shared/DashboardAmountVisibilityToggle';

const VendorDashboard = () => {
 const { user } = useAuth();
 const {
 hasActiveSubscription,
 isChecking,
 currentSubscription: subscriptionDetails,
 checkSubscriptionStatus,
 isLoading: loading,
 refetch: refetchSubscription,
 } = useVendorSubscriptionFeatures();
 const { isVendorVerified, isVerificationPending, isLoading: verificationLoading } =
 useVendorVerification();
 const {
 steps: gettingStartedSteps,
 visible: showGettingStarted,
 dismiss: dismissGettingStarted,
 } = useVendorGettingStarted();
 const navigate = useNavigate();
 const [showRenewalModal, setShowRenewalModal] = useState(false);
 const [period, setPeriod] = useState('month');
 const { amountsVisible, toggleAmountsVisible } = useDashboardAmountVisibility('vendor');

 // Fetch analytics
 const { data: analyticsData, isFetching: analyticsFetching, refetch: refetchAnalytics } = useVendorAnalytics(period);
 const analytics = analyticsData?.data || {};

 // Fetch announcements
 const { data: announcementsData } = useAnnouncements();
 const announcements = announcementsData?.data || [];

 // Fetch recent bookings
 const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
 queryKey: ['vendorRecentBookings', 'internal'],
 queryFn: async () => {
 const response = await api.get(
 '/vendor/bookings?per_page=5&sort_by=created_at&sort_order=desc&channel=internal'
 );
 return response.data;
 }
 });

 const recentBookings = bookingsData?.data?.data || bookingsData?.data || [];

 // Fetch booking statistics — Internal channel only (exclude private/external)
 const { data: statsData } = useQuery({
 queryKey: ['vendorBookingStats', 'internal'],
 queryFn: async () => {
 const response = await api.get('/vendor/bookings/statistics', {
 params: { channel: 'internal' },
 });
 return response.data;
 },
 staleTime: 15_000,
 });

 const stats = statsData?.data || {};

 // Fetch subscription details when component mounts or subscription status changes
 useEffect(() => {
 if (hasActiveSubscription === true) {
 refetchSubscription();
 }
 }, [hasActiveSubscription, user?.id]);

 // Helper functions
 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined || amount === '') return '$0.00';
 const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
 if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
 return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
 };

 const formatDate = (dateString) => {
 if (!dateString) return 'N/A';
 return new Date(dateString).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'short',
 day: 'numeric'
 });
 };

 const getStatusColor = (status) => {
 switch (status) {
 case 'completed':
 return 'bg-emerald-100 text-emerald-800';
 case 'confirmed':
 return 'bg-surface-muted text-ink';
 case 'pending':
 return 'bg-amber-100 text-amber-800';
 case 'in_progress':
 return 'bg-surface-muted text-ink';
 case 'cancelled':
 return 'bg-red-100 text-red-800';
 default:
 return 'bg-gray-100 text-gray-800';
 }
 };

 // Check if subscription is expiring soon
 const isExpiringSoon = () => {
 if (!subscriptionDetails?.days_remaining) return false;
 return subscriptionDetails.days_remaining <= 5 && subscriptionDetails.days_remaining > 0;
 };

 const getUrgencyLevel = () => {
 if (!subscriptionDetails?.days_remaining) return null;
 if (subscriptionDetails.days_remaining <= 1) return 'critical';
 if (subscriptionDetails.days_remaining <= 3) return 'high';
 if (subscriptionDetails.days_remaining <= 5) return 'medium';
 return null;
 };

 const handleRenewalSuccess = async () => {
 await refetchSubscription();
 checkSubscriptionStatus();
 };

 if (isChecking || loading || verificationLoading) {
 return (
 <div className="flex items-center justify-center py-24">
 <div className="text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading dashboard…</p>
 </div>
 </div>
 );
 }

 const periodTabs = [
 { value: 'week', label: 'Week' },
 { value: 'month', label: 'Month' },
 { value: 'quarter', label: 'Quarter' },
 { value: 'year', label: 'Year' },
 ];

 const primaryMetrics = [
 {
 label: 'Total revenue',
 value: formatCurrency(analytics.total_revenue || 0),
 hint: 'Completed bookings',
 icon: DollarSign,
 accent: 'text-emerald-600',
 wrap: 'bg-emerald-50',
 isAmount: true,
 },
 {
 label: 'Total bookings',
 value: stats.total_bookings ?? stats.platform_bookings ?? analytics.total_bookings ?? 0,
 hint: 'Marketplace only',
 icon: Calendar,
 accent: 'text-ink',
 wrap: 'bg-surface-muted',
 isAmount: false,
 },
 {
 label: 'Avg. booking value',
 value: formatCurrency(analytics.average_booking_value || 0),
 hint: 'Per completed booking',
 icon: Star,
 accent: 'text-amber-600',
 wrap: 'bg-amber-50',
 isAmount: true,
 },
 {
 label: 'Pending requests',
 value: stats.pending_bookings || 0,
 hint: 'Awaiting your action',
 icon: Clock,
 accent: 'text-rose-600',
 wrap: 'bg-rose-50',
 isAmount: false,
 },
 ].filter((m) => amountsVisible || !m.isAmount);

 const statusMetrics = [
 { label: 'Confirmed', value: stats.confirmed_bookings || 0 },
 { label: 'In progress', value: stats.in_progress_bookings || 0 },
 { label: 'Completed', value: stats.completed_bookings || 0 },
 { label: 'Cancelled', value: stats.cancelled_bookings || 0 },
 ];

 return (
 <div className="space-y-6 pb-10">
 <VendorPageHeader
 className="mb-0"
 title="Internal dashboard"
 subtitle={`Welcome back, ${user?.name || 'Vendor'}. Marketplace bookings, earnings, and platform performance.`}
 actions={
 <>
 <ChannelBadge channel="internal" />
 <div className="inline-flex rounded-product-md border border-border bg-surface p-1">
 {periodTabs.map((tab) => (
 <button
 key={tab.value}
 type="button"
 onClick={() => setPeriod(tab.value)}
 className={`rounded-product-sm px-3 py-1.5 text-xs font-semibold transition-colors ${ period === tab.value ? 'bg-ink text-white' : 'text-ink-secondary hover:text-ink' }`}
 >
 {tab.label}
 </button>
 ))}
 </div>
 <DashboardAmountVisibilityToggle
 amountsVisible={amountsVisible}
 onToggle={toggleAmountsVisible}
 />
 <button
 type="button"
 onClick={() => refetchAnalytics()}
 disabled={analyticsFetching}
 className="inline-flex items-center gap-2 rounded-product-md border border-border bg-surface px-3.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted disabled:opacity-60"
 >
 <RefreshCw className={`h-4 w-4 ${analyticsFetching ? 'animate-spin' : ''}`} />
 Refresh
 </button>
 {hasActiveSubscription && subscriptionDetails ? (
 <Link
 to="/vendor/my-subscription"
 className="inline-flex items-center gap-2 rounded-product-md bg-ink px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-800"
 >
 <Crown className="h-4 w-4" />
 <span className="max-w-[10rem] truncate">{subscriptionDetails.plan_name || 'Subscription'}</span>
 {subscriptionDetails.days_remaining != null ? (
 <span className="hidden rounded-product-sm bg-surface/15 px-2 py-0.5 text-[11px] font-semibold sm:inline-flex">
 {subscriptionDetails.days_remaining}d left
 </span>
 ) : null}
 </Link>
 ) : null}
 </>
 }
 />

 <Link
 to="/vendor/bookings"
 className="block rounded-product-md border border-border bg-surface p-4 transition-colors hover:bg-surface-muted"
 >
 <div className="mb-2 flex items-center gap-2">
 <Globe className="h-4 w-4 text-ink-tertiary" />
 <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
 Marketplace bookings
 </p>
 </div>
 <p className="text-2xl font-semibold tabular-nums text-ink">
 {stats.platform_bookings ?? 0}
 </p>
 <p className="mt-1 text-xs text-ink-secondary">
 {stats.pending_bookings ?? 0} pending · feeds Accounting & Payouts
 </p>
 </Link>

 <div className="flex flex-wrap gap-2">
 {[
 { to: '/vendor/bookings', label: 'Bookings', Icon: Calendar },
 { to: '/vendor/consultations', label: 'Leads', Icon: Video },
 { to: '/vendor/internal-invoices', label: 'Invoices', Icon: FileText },
 { to: '/vendor/payout-disbursements', label: 'Payouts', Icon: Banknote },
 { to: '/vendor/analytics', label: 'Analytics', Icon: BarChart3 },
 ].map(({ to, label, Icon }) => (
 <Link
 key={to}
 to={to}
 className="inline-flex items-center gap-1.5 rounded-product-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-muted"
 >
 <Icon className="h-3.5 w-3.5 text-ink-tertiary" />
 {label}
 </Link>
 ))}
 </div>

 <p className="text-xs text-ink-tertiary">
 Managing your own clients?{' '}
 <Link to="/vendor/external" className="font-medium text-ink underline-offset-2 hover:underline">
 Open External overview
 </Link>
 {stats.private_bookings != null ? ` · ${stats.private_bookings} private bookings` : ''}
 </p>

 {showGettingStarted && (
 <VendorGettingStarted
 steps={gettingStartedSteps}
 onDismiss={dismissGettingStarted}
 />
 )}

 <VendorScheduleAlerts
 attentionProps={
 verificationLoading
 ? null
 : {
 isVendorVerified,
 isVerificationPending,
 onStartVerification: () => navigate('/vendor/verification'),
 hasActiveSubscription,
 subscriptionDetails,
 isExpiringSoon: isExpiringSoon(),
 urgencyLevel: getUrgencyLevel(),
 onRenew: () => setShowRenewalModal(true),
 onComparePlans: () => navigate('/vendor/subscription'),
 }
 }
 />

 {announcements.length > 0 && (
 <section className="space-y-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
 From the platform
 </p>
 <h2 className="text-lg font-semibold text-ink tracking-tight">
 Announcements
 </h2>
 </div>
 <div className="space-y-3">
 {announcements.slice(0, 3).map((announcement) => (
 <AnnouncementCard key={announcement.id} announcement={announcement} />
 ))}
 </div>
 </section>
 )}

 <section className="space-y-3">
 <div className="flex items-end justify-between gap-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
 Performance
 </p>
 <h2 className="text-lg font-semibold text-ink tracking-tight">
 Key metrics
 </h2>
 </div>
 {analyticsFetching ? (
 <span className="text-xs text-gray-400 inline-flex items-center gap-1.5">
 <Loader2 className="w-3.5 h-3.5 animate-spin" />
 Updating…
 </span>
 ) : !amountsVisible ? (
 <span className="text-xs text-gray-400">Amount cards hidden</span>
 ) : null}
 </div>

 {!amountsVisible ? (
 <p className="text-xs text-ink-tertiary">
 Revenue cards are hidden. Use the eye control in the header to show them again.
 </p>
 ) : null}

 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
 {primaryMetrics.map((m) => {
 const Icon = m.icon;
 return (
 <div
 key={m.label}
 className="product-surface p-5"
 >
 <div className="flex items-start justify-between gap-3">
 <div className="min-w-0">
 <p className="text-xs font-medium text-ink-tertiary">{m.label}</p>
 <p className="mt-2 text-2xl font-bold tracking-tight text-ink tabular-nums">
 {m.value}
 </p>
 <p className="mt-1 text-xs text-ink-tertiary">{m.hint}</p>
 </div>
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.wrap}`}>
 <Icon className={`w-5 h-5 ${m.accent}`} />
 </div>
 </div>
 </div>
 );
 })}
 </div>

 <div className="product-surface p-4">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
 {statusMetrics.map((m) => (
 <div key={m.label} className="rounded-xl bg-surface-muted/60 px-4 py-3">
 <p className="text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">
 {m.label}
 </p>
 <p className="mt-1 text-xl font-semibold text-ink tabular-nums">
 {m.value}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
 <section className="xl:col-span-2 space-y-3">
 <div className="flex items-center justify-between gap-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
 Activity
 </p>
 <h2 className="text-lg font-semibold text-ink tracking-tight">
 Recent bookings
 </h2>
 </div>
 <Link
 to="/vendor/bookings"
 className="text-sm font-medium text-ink hover:text-ink inline-flex items-center gap-1"
 >
 View all
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>

 <div className="product-surface overflow-hidden">
 {bookingsLoading ? (
 <div className="flex items-center justify-center py-16">
 <Loader2 className="w-6 h-6 animate-spin text-ink" />
 </div>
 ) : recentBookings.length === 0 ? (
 <div className="text-center py-16 px-6">
 <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
 <p className="text-sm font-medium text-ink">No bookings yet</p>
 <p className="text-sm text-ink-tertiary mt-1 max-w-sm mx-auto">
 When clients book your packages, they’ll show up here.
 </p>
 </div>
 ) : (
 <div className="divide-y divide-gray-100">
 {recentBookings.slice(0, 5).map((booking) => (
 <div
 key={booking.id}
 className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-surface-muted/80 transition-colors"
 >
 <div className="min-w-0 flex-1">
 <div className="flex flex-wrap items-center gap-2">
 <p className="text-sm font-semibold text-ink truncate">
 {booking.client?.name || booking.client_name || booking.guest_name || 'Client'}
 </p>
 {isPrivateBooking(booking) && <ChannelBadge booking={booking} />}
 <span
 className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${getStatusColor(booking.status)}`}
 >
 {booking.status?.replace('_', ' ')}
 </span>
 </div>
 <p className="text-xs text-ink-tertiary mt-1 truncate">
 {booking.package?.name || 'Package'} · {formatDate(booking.event_date)}
 {booking.event_time ? ` · ${booking.event_time}` : ''}
 </p>
 {isPrivateBooking(booking) && (
 <p className="text-[11px] text-amber-700 mt-1">
 {PRIVATE_PAYOUT_EXCLUDED_NOTE}
 </p>
 )}
 </div>
 <div className="flex items-center gap-3 shrink-0">
 <p className="text-sm font-semibold text-ink tabular-nums">
 {amountsVisible
 ? formatCurrency(
 booking.vendor_payout_amount ??
 booking.subtotal_amount ??
 booking.total_amount ??
 0
 )
 : '••••'}
 </p>
 <Link
 to={`/vendor/bookings/${booking.id}`}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-ink hover:bg-surface-muted"
 >
 <Eye className="w-3.5 h-3.5" />
 Open
 </Link>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </section>

 <section className="space-y-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
 Insights
 </p>
 <h2 className="text-lg font-semibold text-ink tracking-tight">
 Platform performance
 </h2>
 </div>
 <PlatformPerformanceCard
 performance={analytics.platform_performance}
 />
 </section>
 </div>

 <RenewalModal
 isOpen={showRenewalModal}
 onClose={() => setShowRenewalModal(false)}
 subscriptionDetails={subscriptionDetails}
 onSuccess={handleRenewalSuccess}
 />
 </div>
 );
};

export default VendorDashboard;
